"use client";

import type { PropsWithChildren } from "react";
import type { PostHog } from "posthog-js";
import { PostHogProvider as PostHogReactProvider } from "posthog-js/react";

import { PostHogIdentity } from "@/app/PostHogIdentity";
import PostHogPageView from "@/app/PostHogPageView";
import {
  captureDesktopFirstLaunch,
  configureProductAnalytics,
  identifyProductAnalyticsUser,
} from "@/lib/analytics/product-events";
import {
  capturePostHogPageView,
  postHogOptions,
  registerPostHogScopedCaptureClient,
} from "@/lib/analytics/posthog-config";
import { useStore } from "@/stores/appStore";

const postHogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const postHogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;

const options = {
  ...postHogOptions,
  loaded: (client: PostHog) => {
    registerPostHogScopedCaptureClient(client);
    configureProductAnalytics(client);
    identifyProductAnalyticsUser(useStore.getState().user?.userId, client);
    void captureDesktopFirstLaunch({ client });
    capturePostHogPageView(client, window.location.origin, window.location.pathname);
  },
};

export function PostHogProvider({ children }: PropsWithChildren) {
  if (!postHogKey || !postHogHost) {
    return children;
  }

  return (
    <PostHogReactProvider apiKey={postHogKey} options={options}>
      <PostHogIdentity />
      <PostHogPageView />
      {children}
    </PostHogReactProvider>
  );
}
